//utils
import { Settings, LoadingMessages } from '@/constants'
import adapters from '@/utils/ai-adapters/adapters'
import store from '@/utils/store/store'
import MessageQueue from '@/utils/messageQueue'

import {
    updateLastUpdate,
    updateChatWaitsForResponse,
} from '@/utils/store/features/assistants/assistantsSlice'

import { TFunction } from 'i18next'
import { ApiParameters } from '@/shared/types/api'
import { AssistantConfig } from '@/shared/types/space'
import { AssistantChat, ChatId } from './types'
import type { HistoryMessage } from '@/utils/ai-adapters/xdc/types'
import type { Metadata } from '@/ui/advancedAssistants/MMA/types/MMA'
import i18next from 'i18next'
import getBackendLanguageCode from '@/utils/language'

const loadingMessages: Record<string, string> = LoadingMessages

class Assistant {
    config
    metadata: Metadata | undefined
    path
    params: ApiParameters | undefined
    isActive
    isDisabled
    messageQueue: MessageQueue<HistoryMessage[]> | undefined
    chatList: AssistantChat[]
    activeChat: AssistantChat | undefined
    aiAdapter
    dispatch
    loadingMessage
    messageDelay: number = 10
    languageChangeHandler: (lng: string) => void
    translate: TFunction
    suppressNextWelcomeMessage: boolean = false

    constructor(config: AssistantConfig, path: string, translate: TFunction) {
        this.config = config
        this.path = path
        this.params = undefined
        this.isActive = false
        this.isDisabled = false
        this.messageQueue = undefined
        this.metadata = undefined
        this.translate = translate

        this.chatList = [] // would contain {id: ..., msg-list: [{role: {assistant}, content: "..."},... ] }
        this.activeChat = undefined // pointer to the active chat in chatlist
        this.aiAdapter = adapters.create(config.aiConfig)
        this.dispatch = store.dispatch
        this.loadingMessage = loadingMessages[config.id] || translate('chat:loadingHistory') // update

        // watch for language changes
        this.languageChangeHandler = () => {
            if (this.isActive && this.params) {
                this.activate(this.params)
            }
        }
        i18next.on('languageChanged', this.languageChangeHandler)
    }

    destroy = () => {
        i18next.off('languageChanged', this.languageChangeHandler)
    }

    init = async () => {
        this.messageDelay = this.config.appearance?.messageDelay || 100
        this.messageQueue = new MessageQueue<HistoryMessage[]>(this.messageDelay, this.messageHandler)

        await this.aiAdapter.init(this.updateActiveChat)

        if (!this.config.appearance?.icon && !this.config.appearance?.image) {
            this.config.appearance = {
                ...this.config.appearance,
                image: Settings.defaultAssistantImage,
                imageAllowInversion: Settings.defaultAssistantImageAllowInversion,
            }
        }
    }

    // function, which updates redux with timestamp of latest change, to be able to register changes
    registerUpdate = () => {
        if (this.dispatch)
            this.dispatch(updateLastUpdate({ timestamp: Date.now() }))
    }

    syncChatIdInUrl = (chatId: ChatId = undefined) => {
        if (!this.params?.path) return

        const targetPath = `/${this.params.path}`
        if (window.location.pathname !== targetPath) return

        const searchParams = new URLSearchParams(window.location.search)

        if (chatId)
            searchParams.set('chatId', chatId)
        else
            searchParams.delete('chatId')

        const nextSearch = searchParams.toString()
        const nextUrl = nextSearch ? `${targetPath}?${nextSearch}` : targetPath
        const currentUrl = `${window.location.pathname}${window.location.search}`

        if (currentUrl !== nextUrl)
            window.history.replaceState(window.history.state, '', nextUrl)
    }

    activate = async (params: ApiParameters) => {
        const language = i18next.language || localStorage.getItem('i18nextLng') || 'en'
        const baseLanguage = getBackendLanguageCode(language, params.additionalBackendSupportedLanguages)

        this.isActive = true
        this.params = {
            ...params,
            languageCode: baseLanguage,
        }
        if (!this.metadata && 'getMetadata' in this.aiAdapter) {
            const metadataResponse = await this.aiAdapter.getMetadata(this.params)

            this.metadata = metadataResponse?.metadata
        }

        if (!this.activeChat || params.chatId) this.switchChat(params.chatId)
    }

    deactivate = () => {
        this.isActive = false
        this.activeChat = undefined

        this.registerUpdate()
    }

    disable = () => {
        this.isDisabled = true
        this.registerUpdate()
    }

    enable = () => {
        this.isDisabled = false
        this.registerUpdate()
    }

    welcomeMessageTranslation = () => {
        const translationKey = `${this.config.id}:welcomeMessage`;
        const translatedMessage = this.translate(translationKey);

        return translatedMessage !== "welcomeMessage" ? translatedMessage : null
    }

    // if id is undefined, create a new chat and make the new the active chat
    // if id is defined, switch to the chat with the id
    switchChat = async (id: ChatId = undefined) => {
        if (id) {
            const nextActiveChat = this.chatList.find((chat) => chat.chatId === id)

            if (nextActiveChat) {
                if (!nextActiveChat.messageList?.[0]?.message_id) {
                    const chatParams = { ...this.params, chatID: id }
                    const loadingMessage = this.aiAdapter.parseMessage(
                        'assistant',
                        this.loadingMessage,
                        { finishedMessage: true }
                    )

                    nextActiveChat.messageList = [loadingMessage]
                    const allowHistoryLoading =
                        this.config?.aiConfig?.hasHistory
                    if (allowHistoryLoading && 'getHistory' in this.aiAdapter) {
                        await this.aiAdapter
                            .getHistory(0, chatParams)
                            .then((history) => {
                                nextActiveChat.messageList =
                                    history?.messageList?.map((msg) => ({
                                        ...msg,
                                        messageId:
                                            msg?.messageId ?? msg?.message_id,
                                    })) ?? []
                                this.registerUpdate()
                            })
                    }
                }

                this.activeChat = nextActiveChat
                this.syncChatIdInUrl(nextActiveChat.chatId)
            } else {
                console.warn(
                    `[Assistant] chat with id ${id} not found. Creating new chat`
                )
                await this.switchChat()
            }
        } else {
            const defaultWelcomeMessage = this.welcomeMessageTranslation()
            let welcomeMessage

            // create new chat
            this.activeChat = {
                chatId: id,
                name: Settings.emptyChat,
                messageList: [],
                timestamp: Date.now().toString(),
            }
            this.syncChatIdInUrl()

            // welcome to create new chat
            if (defaultWelcomeMessage && !this.suppressNextWelcomeMessage) {
                welcomeMessage = this.aiAdapter.parseMessage(
                    'assistant',
                    defaultWelcomeMessage,
                    { finishedMessage: true }
                )

                this.updateActiveChat([welcomeMessage])
            }
            this.suppressNextWelcomeMessage = false
        }

        this.registerUpdate()
    }

    updateActiveChat = async (messageList: HistoryMessage[]) => {
        if (this.activeChat) {
            this.messageQueue?.addMessage(messageList)
        } else {
            console.warn('[Assistant] active chat is undefined')
        }
    }

    // delete chat
    // if undefined id, assume active chat should be deleted
    deleteChat = (id: ChatId = undefined) => {
        if (this.activeChat?.chatId == id || id === undefined)
            this.activeChat = undefined

        this.chatList = this.chatList.filter((chat) => chat.chatId !== id)
        this.registerUpdate()
    }

    getActiveChat = () => {
        return this.activeChat
    }

    // the history of the assistant can be missing when it could not be loaded
    updateHistory = (history: AssistantChat[] | undefined) => {
        this.chatList = history ?? []
        this.registerUpdate()
    }

    updateChatName = (id: ChatId, name: string) => {
        const chat = this.chatList.find((chat) => chat.chatId === id)

        if (chat?.name) chat.name = name
    }

    updateMessageList = (id: ChatId, messageList: HistoryMessage[]) => {
        const chat = this.activeChat

        if (chat) {
            let isEnlisted = false

            chat.messageList = messageList

            if (id) {
                isEnlisted = this.chatList.some((chat) => chat.chatId === id)
                chat.chatId = id
            }

            if (!isEnlisted) this.chatList.push(chat)
        }
    }

    messageHandler = (messageList: HistoryMessage[] | undefined) => {
        if (!messageList) return

        const lastMessage = messageList[messageList.length - 1]
        const isFinished = lastMessage.finishedMessage
        const isEnlisted = this.chatList?.find(
            (chat) => chat.chatId === this.activeChat?.chatId
        ) // making sure a new chat is only added once

        const activeChat = this.activeChat
        if (!activeChat) {
            console.warn('[Assistant] active chat is undefined')
            return
        }

        if (!activeChat.chatId)
            activeChat.chatId = lastMessage.chatId

        if (activeChat.chatId)
            this.syncChatIdInUrl(activeChat.chatId)

        if (!activeChat.label)
            activeChat.label = lastMessage.label || messageList[0].content

        if (isFinished && activeChat.chatId && !isEnlisted) {
            const newChat = { ...activeChat, path: this.params?.path, spaceId: this.params?.spaceId, assistantId: this.params?.assistantId, icon: this.params?.icon };
            this.chatList.unshift(newChat) // add to beginning
        }

        activeChat.messageList = messageList
        this.registerUpdate()

        // handle waiting for response var, cause here the delay (handelt in messagequeue) is included
        if (isFinished) {
            this.dispatch(updateChatWaitsForResponse({ waiting: false }))
        } else if (!isFinished || lastMessage.role === "assistant") {
            this.dispatch(updateChatWaitsForResponse({ waiting: true }))
        }
    }

    sendAudio = async (audio: Blob, token: string) => {
        if (this.activeChat) {
            const params = {
                ...this.params,
                token: token,
                chatID: this.activeChat.chatId,
            }

            const response = await this.aiAdapter.getAudioTranscription(
                audio,
                params
            )

            return response
        } else {
            console.warn('[Assistant] active chat is undefined')
        }
    }
}

export default Assistant
